import { CameraMode, Globe, EquatorialCoord, utils } from "@hscmap/stellar-globe";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { compare as compareLayer } from "../layer_component_base";
import { KEYS } from "../key";
import { IView } from '@/store/frame';
import { queryParams } from '@/utils/query_params';

const defaultView: IView = {
  a: 0,
  d: 0,
  fovy: 2,
  roll: 0,
}

@Component({
  provide() {
    return { [KEYS.GlobeComponent]: this }
  },
})
export class GlobeComponentType extends Vue {
  globe!: Globe

  @Prop({ default: true })
  defaultLayers!: boolean

  beforeCreate() {
    this.globe = new Globe(undefined, false, true)
    this.globe.setLayerSortFunc(compareLayer)
    this.globe.ee.on('camera-move-end', () => this.emitUpdateView())
    this.$emit('load', this.globe)
  }

  mounted() {
    this.globe.mount(this.$refs.globeElement as HTMLElement)
    Vue.nextTick(() => this.globe.resize())
  }

  beforeDestroy() {
    this.globe.release()
  }

  resize() {
    this.globe.resize()
  }

  // retina
  @Prop({ type: Boolean, default: false })
  retina!: boolean

  @Watch('retina', { immediate: true })
  onRetinaChange(retina: boolean) {
    this.globe.viewFactory.setRetina(retina)
  }

  // cameraMode
  @Prop({ default: CameraMode.STEROGRAPHIC })
  cameraMode!: CameraMode

  @Watch('cameraMode', { immediate: true })
  onCameraModeChange(cameraMode: CameraMode) {
    this.globe.viewFactory.changeMode(cameraMode, 400)
  }

  // view
  @Prop({ default: () => defaultView })
  view!: IView

  @Watch('view', { immediate: true, deep: true })
  onViewChange(view: IView) {
    const vf = this.globe.viewFactory
    const [theta, phi] = vf.coord2thetaphi(EquatorialCoord.fromRad(view.a, view.d))
    vf.theta = theta
    vf.phi = phi
    vf.fovy = view.fovy
    vf.roll = view.roll
    this.globe.requestRefresh()
  }

  @Prop({ default: 200 })
  viewUpdateDelay!: number
  @Watch('viewUpdateDelay', { immediate: true })
  onViewUpdateDelay(delay: number) {
    this.onCameraMoveThrottled && this.globe.ee.off('camera-move', this.onCameraMoveThrottled)
    this.globe.ee.on('camera-move', this.onCameraMoveThrottled = utils.throttle(delay, () => this.emitUpdateView()))
  }
  private onCameraMoveThrottled?: () => void

  private emitUpdateView() {
    const vf = this.globe.viewFactory
    const coord = vf.thetaphi2coord(vf.theta, vf.phi)
    const view: IView = {
      a: coord.a.rad,
      d: coord.d.rad,
      fovy: vf.fovy,
      roll: vf.roll,
    }
    this.$emit('update:view', view)
  }
}
